import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../config/queryKeys'
import { listMeFamily } from '../services/user/listMeFamily'

type Image = {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
  formats?: {
    id: string
    filename: string
    full_path: string
    mime_type: string
    width: number
    height: number
  }[]
}

export type Player = {
  id: string
  name_first: string
  name_middle: string | null
  name_last: string
  full_name?: string | null
  birth_date?: string | null
  height?: number | null
  weight?: number | null
  created_at?: string
  gender?: string | null
  image: Image | null
  invitation_id?: string | null
  player_number: string | null
  play_direction: string | null
  is_affiliate: boolean
  is_injured: boolean
  is_playing: boolean
  is_starting: boolean
  is_proposed_starting: boolean
  is_suspended: boolean
  attendance_status: string | null
  customer_identifier: string
  sf_id: string | null
  suspensions: any[]
  email?: string
  phone_number?: string
  is_accepted?: boolean
  is_invited?: boolean
  is_validated?: boolean
  is_signed_waiver: boolean
  league_registration_number: string | null
  player_type: string | null
}

const STALE_TIME = 30 * 1000

/**
 * The current user's linked player profiles (GET /users/me/family). Backed by
 * React Query and keyed on `userFamily.me`, so it shares one cache with the
 * Profile screen's `useMeFamily`. Keeps the legacy `{ data, loading, error,
 * refetch }` shape its consumers expect, typed as the richer `Player[]` view
 * of the same payload.
 *
 * @param enabled gate the request (e.g. only while logged-in + on registration).
 *                Defaults to true.
 */
export const useUserPlayers = (enabled = true) => {
  'use memo'
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.userFamily.me(),
    queryFn: listMeFamily,
    staleTime: STALE_TIME,
    enabled,
  })

  return {
    data: (data as Player[] | undefined) ?? null,
    loading: enabled && (isPending || isFetching),
    error: (error as Error) ?? null,
    refetch,
  }
}
